import type { PhoneNumber } from "@/atoms-react/input/InputPhone.react";
import type { CguType } from "@/features/cgu/types";
import {
  SignupType,
  type DeclarativeBirth,
  type DeclarativeIdentity,
  type DeclarativeIncomeSource,
  type DeclarativeMonthlyIncome,
  type DeclarativeNif,
  type DeclarativeOccupation,
  type DeclarativePerson,
  type DeclarativeResidence
} from "@/features/flow/types";
import type { SignupState } from "@/models/signup";
import { createSlice, type PayloadAction, type WritableDraft } from "@reduxjs/toolkit";

type SetPhonePayload = {
  phone: WritableDraft<PhoneNumber>;
  cguItem: CguType;
};

export const SIGNUP_SLICE_INITIAL: SignupState = {
  isLaymoonFlowGoingBackFromPasswordStep: false,
  phone: null,
  cguItem: null,
  signup_type: null,
  security: {
    isWebauthn: false,
    password: null
  },
  identity: {},
  taxAddress: [],
  jwt_otp: null,
  mirror_uuid: null,
  withPhone: false
};

export const signupSlice = createSlice({
  name: "signup",
  initialState: SIGNUP_SLICE_INITIAL,

  reducers: {
    setPhone: (state, action: PayloadAction<SetPhonePayload>) => {
      state.phone = action.payload.phone;
      state.signup_type = SignupType.individual;
      state.cguItem = action.payload.cguItem;
    },
    setDeclarativeIdentity: (state, action: PayloadAction<DeclarativeIdentity>) => {
      state.identity = action.payload;
    },
    setPassword: (state, action: PayloadAction<string>) => {
      state.security.isWebauthn = false;
      state.security.password = action.payload;
    },
    setDeclarativeIdentityWelcome: (state) => {
      state.identity = { ...state.identity, welcome: true };
    },
    setTokenOtp: (state, action: PayloadAction<string | null>) => {
      state.jwt_otp = action.payload;
    },
    setwithPhone: (state, action: PayloadAction<boolean>) => {
      state.withPhone = action.payload;
    },
    setMirrorUuid: (state, action: PayloadAction<string | null>) => {
      state.mirror_uuid = action.payload;
    },
    setDeclarativeIdentityPerson: (state, action: PayloadAction<DeclarativePerson>) => {
      const { gender, firstnames, lastnames, commonFirstName, commonName, otherfirstnames } =
        action.payload;
      state.identity = {
        ...state.identity,
        person: {
          //we don't want to override nationalityCodes
          nationalityCodes: state.identity?.person?.nationalityCodes ?? [],
          gender,
          firstnames,
          lastnames,
          commonFirstName,
          commonName,
          otherfirstnames
        }
      };
    },
    setDeclarativeIdentityPersonNationalityCodes: (state, action: PayloadAction<string[]>) => {
      state.identity = {
        ...state.identity,
        person: { ...state.identity?.person, nationalityCodes: action.payload }
      };
    },
    setDeclarativeIdentityTaxAdress: (state, action: PayloadAction<DeclarativeNif[]>) => {
      state.identity = {
        ...state.identity,
        taxAdress: action.payload
      };
    },
    setDeclarativeIdentityAddressBirth: (state, action: PayloadAction<DeclarativeBirth>) => {
      let birthDate = action.payload.birth;

      if (action.payload.birth) {
        const day = action.payload.birth.getDate();
        const month = action.payload.birth.getMonth() + 1;
        const year = action.payload.birth.getFullYear();

        const birthTest = month + "/" + day + "/" + year;
        birthDate = new Date(birthTest);
      }

      state.identity = {
        ...state.identity,
        addressBirth: {
          ...action.payload,
          birth: birthDate
        }
      };
    },
    setDeclarativeIdentityAddressResidence: (
      state,
      action: PayloadAction<DeclarativeResidence>
    ) => {
      state.identity = {
        ...state.identity,
        addressResidence: action.payload
      };
    },
    setDeclarativeIdentityOccupation: (state, action: PayloadAction<DeclarativeOccupation>) => {
      state.identity = {
        ...state.identity,
        occupation: action.payload
      };
    },
    setDeclarativeIdentityMonthlyIncome: (
      state,
      action: PayloadAction<DeclarativeMonthlyIncome>
    ) => {
      state.identity = {
        ...state.identity,
        monthlyIncome: action.payload
      };
    },
    setDeclarativeIdentitySourcesIncome: (
      state,
      action: PayloadAction<DeclarativeIncomeSource[]>
    ) => {
      state.identity = {
        ...state.identity,
        sourcesIncome: action.payload
      };
    },
    setIsLaymoonFlowGoingBackFromPasswordStep: (state, action: PayloadAction<boolean>) => {
      state.isLaymoonFlowGoingBackFromPasswordStep = action.payload;
    },
    resetSignup: () => SIGNUP_SLICE_INITIAL
  },
  selectors: {
    getDeclarativeIdentity: (state): DeclarativeIdentity => state.identity ?? {},
    getOtpToken: (state): string | null => state.jwt_otp,
    getMirrorUuid: (state): string | null => state.mirror_uuid,
    getwithPhone: (state): boolean => state.withPhone
  }
});

export const {
  setPhone,
  setDeclarativeIdentity,
  setPassword,
  resetSignup,
  setDeclarativeIdentityWelcome,
  setDeclarativeIdentityPerson,
  setDeclarativeIdentityPersonNationalityCodes,
  setDeclarativeIdentityTaxAdress,
  setDeclarativeIdentityAddressBirth,
  setDeclarativeIdentityAddressResidence,
  setDeclarativeIdentityOccupation,
  setDeclarativeIdentityMonthlyIncome,
  setDeclarativeIdentitySourcesIncome,
  setMirrorUuid,
  setTokenOtp,
  setwithPhone,
  setIsLaymoonFlowGoingBackFromPasswordStep
} = signupSlice.actions;

export const { getDeclarativeIdentity, getOtpToken, getMirrorUuid, getwithPhone } =
  signupSlice.selectors;
